import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Mic,
  Undo2,
  UserRound,
  History,
  MessagesSquare,
  Languages,
  Lightbulb,
  Loader2,
  BadgeCheck,
  Info,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { languoidEnglishName } from "@kolectra/shared/languoids";
import {
  Connectedness,
  Preparedness,
  SocialDifferential,
  decodeRegister,
} from "@kolectra/shared/registers";
import {
  ChallengeState,
  TERMINAL_STATES,
  type ChallengeStateName,
} from "@kolectra/shared/states";
import { Permission } from "@kolectra/shared/permissions";
import type {
  PreTestReview,
  PreTestTake,
  PreTestHistoryEvent,
  UtteranceSummary,
} from "@kolectra/shared/schemas";
import { useWebPerms } from "../auth.js";
import {
  challengesApi,
  ApiError,
  newIdempotencyKey,
} from "../lib/api-client.js";

const STATE_LABEL: Record<ChallengeStateName, string> = {
  DRAFT: "Draft",
  READY_TO_RECORD: "To record",
  RECORDING: "Recording",
  READY_TO_TEST: "To test",
  TESTED: "Tested",
  JUDGED: "Judged",
  AWAITING_SECOND_REVIEW: "2nd review",
  DISPUTED: "Disputed",
  APPROVED: "Completed",
};

function registerTitle(code: string): string {
  const t = decodeRegister(code);
  if (!t) return code;
  return [
    Connectedness[t.connectedness]?.name,
    Preparedness[t.preparedness]?.name,
    SocialDifferential[t.socialDifferential]?.name,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * Pre-test review (web Dashboard). A reviewer/admin inspects what matters to
 * judge the audio and the challenge BEFORE it is tested: the utterance scripts +
 * their translations, the comprehension question, and the audio previews — then
 * acts. The "remaining" context (scene, registers, design notes, who-did-what)
 * is tucked into a collapsible "Additional metadata" card.
 *
 * Actions are gated by permission + state:
 *   - can_force_rework     → Approve segment (publish) | Rework (→ designer)
 *   - can_reject_recording → Approve audio (→ ready to test) | Re-record
 *
 * There is deliberately NO model output, params, or cost here.
 */
export function PreTestReviewDetail({
  review,
  teamId,
}: {
  review: PreTestReview;
  teamId: string;
}) {
  const { challenge } = review;
  const state = challenge.state;

  return (
    <div>
      <div className="lockbar" style={{ marginBottom: 18 }}>
        <Languages strokeWidth={1.8} />
        <span>
          {challenge.segmentId} ·{" "}
          {languoidEnglishName(challenge.languoidCode) ??
            challenge.languoidCode}{" "}
          · {challenge.domain}
        </span>
        <span
          className={`statepill sp-${state}`}
          style={{ marginLeft: "auto" }}
        >
          {STATE_LABEL[state]}
        </span>
      </div>

      <ReviewCard review={review} />

      <AdditionalMetadata review={review} />

      {/* temporary hidden */}
      {/* <Actions review={review} teamId={teamId} /> */}
    </div>
  );
}

/**
 * What's needed to judge the audio and the challenge: per-utterance text +
 * translation, the comprehension question, then the audio previews.
 */
function ReviewCard({ review }: { review: PreTestReview }) {
  return (
    <div className="zone meta" style={{ marginBottom: 16 }}>
      <div className="zhead">
        <div className="zico">
          <MessagesSquare size={14} strokeWidth={2} />
        </div>
        <div className="ztitle">Segment &amp; audio</div>
        <div className="zdesc">{review.challenge.domain}</div>
      </div>

      {review.utterances.length > 0 ? (
        <div>
          {review.utterances.map((u, i) => (
            <UtteranceTextRow
              key={u.id}
              u={u}
              first={i === 0}
              canViewGold={review.canViewGold}
            />
          ))}
        </div>
      ) : (
        <GoldField
          label="Reference translation (gold)"
          value={review.goldTranslation}
          canViewGold={review.canViewGold}
        />
      )}

      {review.questionText && (
        <div className="field" style={{ marginTop: 12, marginBottom: 12 }}>
          <label>Comprehension question</label>
          <div className="jtext">{review.questionText}</div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          marginTop: 12,
          background: "var(--data-bg)",
          border: "1px solid var(--data-edge)",
          borderRadius: 8,
          padding: "10px 12px",
        }}
      >
        <span
          className="mini"
          style={{
            color: "var(--data)",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Mic size={13} strokeWidth={2} /> Audio previews
        </span>
        {review.recordings.map((t) => (
          <TakeRow key={t.kind} take={t} />
        ))}
      </div>
    </div>
  );
}

/** One utterance's speaker + script + translation — the QC essentials. */
function UtteranceTextRow({
  u,
  first,
  canViewGold,
}: {
  u: UtteranceSummary;
  first: boolean;
  canViewGold: boolean;
}) {
  return (
    <div
      style={{
        borderTop: first ? 0 : "1px dashed var(--meta-edge)",
        paddingTop: first ? 0 : 12,
        marginTop: first ? 0 : 12,
      }}
    >
      <div style={{ marginBottom: 5 }}>
        <span className="permchip">{u.speakerId}</span>
      </div>
      {u.text && <div className="jtext">{u.text}</div>}
      <div className="field" style={{ marginTop: 6, marginBottom: 0 }}>
        <label>Translation</label>
        {canViewGold ? (
          <div className="jtext" style={{ color: "var(--meta)" }}>
            {u.referenceTranslation || (
              <span className="mini">— not provided —</span>
            )}
          </div>
        ) : (
          <div className="mini">Hidden — you don’t hold can_view_gold.</div>
        )}
      </div>
    </div>
  );
}

function TakeRow({ take }: { take: PreTestTake }) {
  const fmt = (ms: number | null) =>
    ms != null ? ` · ${(ms / 1000).toFixed(1)}s` : "";
  const meta: Record<PreTestTake["status"], { text: string; color: string }> = {
    CONFIRMED: {
      text: `Confirmed${fmt(take.durationMs)}`,
      color: "var(--good)",
    },
    DRAFT: { text: "Draft take (not confirmed)", color: "var(--warn)" },
    // Recorded + in the team's Drive, but not yet ingested (awaiting the reviewed
    // URL in the sheet) — better than the stagnant "Not recorded".
    NONE: take.uploadedAt
      ? { text: "Uploaded to Drive · awaiting review", color: "var(--warn)" }
      : { text: "Not recorded", color: "var(--ink-faint)" },
  };
  const m = meta[take.status];
  const label = take.kind === "SEGMENT" ? "Segment audio" : "Question audio";
  return (
    <div style={{ fontSize: 12.5 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ color: "var(--ink-soft)", minWidth: 110 }}>{label}</span>
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: m.color,
            flex: "none",
          }}
        />
        <span style={{ color: m.color }}>{m.text}</span>
        {take.recorderName && (
          <span className="mini" style={{ marginLeft: "auto" }}>
            by {take.recorderName}
          </span>
        )}
      </div>
      {take.rejectionNote && (
        <div className="mini" style={{ color: "var(--bad)", marginLeft: 118 }}>
          last rejection: “{take.rejectionNote}”
        </div>
      )}
      {take.audioUrl && (
        <audio
          controls
          preload="none"
          src={take.audioUrl}
          style={{ height: 32, marginTop: 6, marginLeft: 118, maxWidth: 320 }}
        />
      )}
    </div>
  );
}

/**
 * The "remaining" context, collapsed by default: scene/setup, per-utterance
 * register + director's note, the design difficulty + challenge notes, the gold
 * answer, and the who-did-what trail.
 */
function AdditionalMetadata({ review }: { review: PreTestReview }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="zone" style={{ marginBottom: 16 }}>
      <button
        className="btn ghost"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 8px",
        }}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Info size={14} strokeWidth={1.8} /> Additional metadata
      </button>

      {open && (
        <div style={{ marginTop: 12 }}>
          {review.segmentContext && (
            <div className="field" style={{ marginBottom: 12 }}>
              <label>Scene / setup</label>
              <div className="jtext" style={{ color: "var(--ink-soft)" }}>
                {review.segmentContext}
              </div>
            </div>
          )}

          {review.utterances.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <label
                className="mini"
                style={{ display: "block", marginBottom: 6 }}
              >
                Registers &amp; director’s notes
              </label>
              {review.utterances.map((u) => (
                <div
                  key={u.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                    marginBottom: 4,
                  }}
                >
                  <span className="permchip">{u.speakerId}</span>
                  <span className="tag" title={registerTitle(u.registerLabel)}>
                    register {u.registerLabel}
                  </span>
                  {u.utteranceContext && (
                    <span className="mini">{u.utteranceContext}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          <DesignNotes review={review} />

          <WhoDidWhat review={review} />
        </div>
      )}
    </div>
  );
}

/** Difficulty levels, design challenge notes, and the gold reasoning answer. */
function DesignNotes({ review }: { review: PreTestReview }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="tasklabel">
        Translation
        <span
          className={
            "lvl " +
            (review.translationLevel === "EXTRA_HARD"
              ? "tag xhard"
              : "tag hard")
          }
        >
          {review.translationLevel === "EXTRA_HARD" ? "extra hard" : "hard"}
        </span>
      </div>
      <GoldField
        label="Translation challenge"
        value={review.translationChallengeDescription}
        canViewGold={review.canViewGold}
        hint="what makes it hard"
      />

      <div className="tasklabel" style={{ marginTop: 12 }}>
        Reasoning
        <span
          className={
            "lvl " +
            (review.reasoningLevel === "EXTRA_HARD" ? "tag xhard" : "tag hard")
          }
        >
          {review.reasoningLevel === "EXTRA_HARD" ? "extra hard" : "hard"}
        </span>
        <Lightbulb
          size={13}
          strokeWidth={2}
          style={{ color: "var(--ink-faint)" }}
        />
      </div>
      <GoldField
        label="Gold answer"
        value={review.goldAnswer}
        canViewGold={review.canViewGold}
      />
      <GoldField
        label="Reasoning challenge"
        value={review.reasoningChallengeDescription}
        canViewGold={review.canViewGold}
        hint="what makes it hard"
      />
    </div>
  );
}

/** Designer, current holder, and the lifecycle trail of who did what. */
function WhoDidWhat({ review }: { review: PreTestReview }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <label
        className="mini"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 6,
        }}
      >
        <UserRound size={13} strokeWidth={2} /> Who did what
      </label>
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: 16, fontSize: 12.5 }}
      >
        <div>
          <div className="mini">Designer</div>
          <div style={{ fontWeight: 500 }}>{review.designer.name}</div>
        </div>
        {review.currentAssignee && (
          <div>
            <div className="mini">Currently held by</div>
            <div style={{ fontWeight: 500 }}>{review.currentAssignee.name}</div>
          </div>
        )}
      </div>

      {review.history.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button
            className="btn ghost"
            onClick={() => setOpen((v) => !v)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 8px",
            }}
          >
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <History size={14} strokeWidth={1.8} /> History (
            {review.history.length})
          </button>
          {open && (
            <div
              style={{
                marginTop: 10,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {review.history.map((h, i) => (
                <HistoryRow key={i} h={h} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HistoryRow({ h }: { h: PreTestHistoryEvent }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        fontSize: 12.5,
      }}
    >
      <span className={`statepill sp-${h.toState}`} style={{ flex: "none" }}>
        {STATE_LABEL[h.toState] ?? h.toState}
      </span>
      <div>
        <div>
          <b>{h.actorName}</b>
          {h.fromState && (
            <span className="mini">
              {" "}
              · from {STATE_LABEL[h.fromState] ?? h.fromState}
            </span>
          )}
          <span className="mini"> · {new Date(h.at).toLocaleString()}</span>
        </div>
        {h.reason && (
          <div style={{ color: "var(--ink-soft)", marginTop: 2 }}>
            “{h.reason}”
          </div>
        )}
      </div>
    </div>
  );
}

function GoldField({
  label,
  value,
  canViewGold,
  hint,
}: {
  label: string;
  value: string | null;
  canViewGold: boolean;
  hint?: string;
}) {
  return (
    <div className="field" style={{ marginBottom: 10 }}>
      <label>
        {label} {hint && <span className="hint">{hint}</span>}
      </label>
      {canViewGold ? (
        <div className="jtext">
          {value || <span className="mini">— not provided —</span>}
        </div>
      ) : (
        <div className="mini">Hidden — you don’t hold can_view_gold.</div>
      )}
    </div>
  );
}

/**
 * The four pre-test actions at the bottom, each shown only when the actor's
 * permission AND the current state allow it:
 *   - Approve segment (publish): can_force_rework, at DRAFT      → submit
 *   - Rework (→ designer):       can_force_rework, non-DRAFT     → rework
 *   - Approve audio:             can_reject_recording, RECORDING → confirm-recording
 *   - Re-record (segment audio): can_reject_recording, RECORDING/READY_TO_TEST
 */
function Actions({
  review,
  teamId,
}: {
  review: PreTestReview;
  teamId: string;
}) {
  const perms = useWebPerms();
  const qc = useQueryClient();
  const state = review.challenge.state;
  const id = review.challenge.id;
  const isTerminal = TERMINAL_STATES.includes(state);

  const mayRework = perms.has(Permission.CAN_FORCE_REWORK);
  const mayReject = perms.has(Permission.CAN_REJECT_RECORDING);

  // The design gate is at DRAFT: a reviewer either publishes the segment or
  // returns it to the designer with notes. Both are DRAFT-only.
  const canApproveSegment = mayRework && state === ChallengeState.DRAFT;
  const canRework = mayRework && state === ChallengeState.DRAFT;
  const canApproveAudio = mayReject && state === ChallengeState.RECORDING;
  const canReRecord =
    mayReject &&
    (state === ChallengeState.RECORDING ||
      state === ChallengeState.READY_TO_TEST);

  // Approve audio needs the segment (+question, if any) confirmed first.
  const seg = review.recordings.find((t) => t.kind === "SEGMENT");
  const q = review.recordings.find((t) => t.kind === "QUESTION");
  const audioReady =
    seg?.status === "CONFIRMED" && (!q || q.status === "CONFIRMED");

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["challenges", teamId] });
    void qc.invalidateQueries({ queryKey: ["pre-test-review", id] });
  };

  if (isTerminal) {
    return (
      <div className="banner info" style={{ marginTop: 4 }}>
        This challenge is approved — nothing to action.
      </div>
    );
  }

  if (!canApproveSegment && !canRework && !canApproveAudio && !canReRecord) {
    return (
      <div className="banner info" style={{ marginTop: 4 }}>
        Review only — no actions available in this state for your role.
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginTop: 4,
      }}
    >
      {(canApproveSegment || canApproveAudio) && (
        <div className="toolbar" style={{ flexWrap: "wrap", gap: 10 }}>
          {canApproveSegment && (
            <ApproveAction
              label="Approve segment (publish)"
              successLabel="Published for recording."
              mutationFn={() => challengesApi.submit(id, newIdempotencyKey())}
              onDone={invalidate}
            />
          )}
          {canApproveAudio && (
            <ApproveAction
              label="Approve audio"
              successLabel="Approved — ready to test."
              disabled={!audioReady}
              disabledHint="The segment audio isn’t confirmed yet."
              mutationFn={() =>
                challengesApi.confirmRecording(id, newIdempotencyKey())
              }
              onDone={invalidate}
            />
          )}
        </div>
      )}

      {canRework && (
        <SendBackAction
          tone="bad"
          icon={<Undo2 size={15} strokeWidth={1.8} />}
          openLabel="Rework (return to designer)"
          title="Reason for rework"
          hint="required — the designer sees this"
          placeholder="e.g. The gold translation doesn’t match the register — please revise."
          confirmLabel="Confirm rework"
          successLabel="Returned to the designer for rework."
          mutationFn={(comment) =>
            challengesApi.rework(id, newIdempotencyKey(), comment)
          }
          onDone={invalidate}
        />
      )}
      {canReRecord && (
        <SendBackAction
          tone="warn"
          icon={<Mic size={15} strokeWidth={1.8} />}
          openLabel="Re-record — reject the segment audio"
          title="Reason for re-record"
          hint="required — the recorder sees this"
          placeholder="e.g. The segment audio is clipped at the start — please re-record."
          confirmLabel="Confirm — reject audio"
          successLabel="Sent back for a re-record."
          mutationFn={(comment) =>
            challengesApi.rejectSegmentAudio(id, newIdempotencyKey(), comment)
          }
          onDone={invalidate}
        />
      )}
    </div>
  );
}

/** A direct (no-comment) approval button with success/error states. */
function ApproveAction({
  label,
  successLabel,
  mutationFn,
  onDone,
  disabled,
  disabledHint,
}: {
  label: string;
  successLabel: string;
  mutationFn: () => Promise<unknown>;
  onDone: () => void;
  disabled?: boolean;
  disabledHint?: string;
}) {
  const mut = useMutation({
    mutationFn: () => mutationFn(),
    onSuccess: onDone,
  });

  if (mut.isSuccess) {
    return (
      <span className="chip confirmed">
        <BadgeCheck size={13} strokeWidth={2} /> {successLabel}
      </span>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <button
        className="btn pri"
        disabled={disabled || mut.isPending}
        onClick={() => mut.mutate()}
        title={disabled ? disabledHint : undefined}
      >
        {mut.isPending ? (
          <Loader2 size={15} className="spin" />
        ) : (
          <BadgeCheck size={15} strokeWidth={1.8} />
        )}
        {label}
      </button>
      {disabled && disabledHint && <span className="mini">{disabledHint}</span>}
      {mut.isError && (
        <span className="mini" style={{ color: "var(--bad)" }}>
          {mut.error instanceof ApiError
            ? mut.error.message
            : "Couldn’t do that."}
        </span>
      )}
    </div>
  );
}

function SendBackAction({
  tone,
  icon,
  openLabel,
  title,
  hint,
  placeholder,
  confirmLabel,
  successLabel,
  mutationFn,
  onDone,
}: {
  tone: "bad" | "warn";
  icon: React.ReactNode;
  openLabel: string;
  title: string;
  hint: string;
  placeholder: string;
  confirmLabel: string;
  successLabel: string;
  mutationFn: (comment: string) => Promise<unknown>;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const color = tone === "bad" ? "var(--bad)" : "var(--warn)";

  const mut = useMutation({
    mutationFn: () => mutationFn(comment.trim()),
    onSuccess: onDone,
  });

  if (mut.isSuccess) {
    return (
      <span className="chip" style={{ color: "var(--ink-soft)" }}>
        {icon} {successLabel}
      </span>
    );
  }

  if (!open) {
    return (
      <button
        className="btn ghost"
        style={{ alignSelf: "flex-start" }}
        onClick={() => setOpen(true)}
      >
        {icon} {openLabel}
      </button>
    );
  }

  return (
    <div className="zone" style={{ padding: 14 }}>
      <label className="mini" style={{ display: "block", marginBottom: 6 }}>
        {title} <span className="hint">{hint}</span>
      </label>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={placeholder}
        style={{ marginBottom: 8 }}
      />
      {mut.isError && (
        <div className="banner bad" style={{ marginBottom: 8 }}>
          {mut.error instanceof ApiError
            ? mut.error.message
            : "Couldn’t send it back."}
        </div>
      )}
      <div className="toolbar" style={{ justifyContent: "flex-end" }}>
        <button
          className="btn ghost"
          onClick={() => {
            setOpen(false);
            setComment("");
          }}
        >
          Cancel
        </button>
        <button
          className="btn"
          style={{ color, borderColor: color }}
          disabled={!comment.trim() || mut.isPending}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? <Loader2 size={15} className="spin" /> : icon}
          {confirmLabel}
        </button>
      </div>
    </div>
  );
}
